import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useEndAllSessions } from '@tinct/core-api';
import { useUserIdFromToken } from '@tinct/core-store-oauth';
import {
  Button,
  Card,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@tinct/web-components';

/**
 * Signs the user out of every device at once (B-275): a lost laptop, a shared computer. Every
 * session ends, this one included, so the browser goes through the sign-out afterwards.
 */
export const SessionsSection = () => {
  const navigate = useNavigate();
  const userId = useUserIdFromToken();
  const [open, setOpen] = useState(false);

  const endAllSessions = useEndAllSessions({
    onSuccess: () => {
      setOpen(false);
      navigate('/logout');
    },
    onError: (error) => toast.error(error.error || 'Could not sign you out of your devices, try again'),
  });

  return (
    <Card className="rounded-2xl">
      <CardHeader>
        <CardTitle className="text-xl">Sessions</CardTitle>
        <CardDescription>
          Signed in on a device you no longer use or trust? Sign out everywhere, this browser included.
        </CardDescription>
      </CardHeader>
      <CardFooter className="justify-end border-t">
        <Button type="button" variant="outline" onClick={() => setOpen(true)}>
          Sign out of all devices
        </Button>
      </CardFooter>

      <Dialog open={open} onOpenChange={setOpen}>
        {/* p-8: the padding the shared close button is placed for (top-8 right-8) */}
        <DialogContent className="p-8 sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Sign out of all devices</DialogTitle>
            <DialogDescription>
              Every session ends at once: the web app, the back office, and any assistant connected to
              your account. You will need to sign in again here too.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              isLoading={endAllSessions.isPending}
              disabled={!userId}
              onClick={() => userId && endAllSessions.mutate(userId)}
            >
              Sign out everywhere
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
